import React from 'react'
import classNames from 'classnames'
import { Media } from '@/payload-types'
import { Icon } from '@/ui/atoms/Icon'
import { Label } from '@/ui/atoms/Label'
import { Avatar } from '@/ui/atoms/Avatar'
import { getResourceIcon } from '@/utils/resourceIconMap'
import Author from '@/ui/atoms/Author'
import { CMSButton } from '@/ui/components/CMSButton'
import { Button } from '@/ui/atoms/Button'

export type ResourceCardProps = {
  title: string
  description?: string
  date?: Date | string | null
  fileType?: string
  resourceType?: string | null
  tag?: string
  author?: {
    name: string
    position?: string
    avatar?: Media | string | null
    verified?: boolean
    shortDescription?: string | null
  }
  authors?: Array<{
    name: string
    position?: string
    avatar?: Media | string | null
    verified?: boolean
    shortDescription?: string | null
  }>
  price?: number
  metainfo?: string | null
  downloadUrl?: string
  href?: string | null
  className?: string
}

export const ResourceCard: React.FC<ResourceCardProps> = (props) => {
  const {
    title,
    description,
    date,
    fileType,
    resourceType,
    tag,
    author,
    authors,
    price = 0,
    metainfo,
    downloadUrl,
    href,
    className: classNameFromProps,
  } = props

  // Use authors array if provided, otherwise fall back to single author
  const authorsToDisplay = authors && authors.length > 0 ? authors : author ? [author] : []

  const formattedDate = date ? new Date(date).toLocaleDateString('de-DE') : null
  const iconName = getResourceIcon(resourceType)

  return (
    <div
      className={classNames(
        'flex flex-col rounded-md overflow-hidden group bg-white border border-border-subtle',
        classNameFromProps,
      )}
    >
      <div className="flex flex-col p-4 relative h-full">
        {/* Top section: Icon and Badge */}
        <div className="flex items-start justify-between mb-6">
          <div
            className={
              'size-9 rounded-sm flex items-center justify-center relative overflow-hidden bg-bg-brand-blue'
            }
          >
            <Icon name={iconName} parentClass={'z-20'} className={'text-text-accent !size-5'} />
          </div>

          {/* Free Badge */}
          {price === 0 ? (
            <Label
              title="Free"
              variant="success"
              size="small"
              className="bg-green-90 text-green-20"
            />
          ) : (
            <Label
              title={`${price} $`}
              variant="neutral"
              size="small"
              className="bg-neutral-90 text-neutral-20"
            />
          )}
        </div>

        {/* Title */}
        <h3 className="text-title-S-medium text-text-base mb-4 transition-colors">{title}</h3>

        {/* Description */}
        {description && (
          <p className="text-body-S-regular text-text-faded mb-4 line-clamp-3">{description}</p>
        )}

        {/* Date, File Type, and Meta Info */}
        {(formattedDate || fileType || metainfo) && (
          <div className="flex items-center gap-2 text-label-XS-regular text-text-faded mb-4">
            {formattedDate && <span>{formattedDate}</span>}
            {formattedDate && (fileType || metainfo) && (
              <div className="w-1 h-1 rounded-full bg-text-faded" />
            )}
            {fileType && <span>{fileType}</span>}
            {fileType && metainfo && <div className="w-1 h-1 rounded-full bg-text-faded" />}
            {metainfo && <span>{metainfo}</span>}
          </div>
        )}

        {/* Tag */}
        {tag && (
          <div className="mb-6">
            <Label title={tag} variant="neutral" size="small" className="bg-blue-90 text-blue-20" />
          </div>
        )}

        {/* Author */}
        {authorsToDisplay.length > 0 && (
          <div className="flex items-center gap-3 bg-bg-subtle p-2 rounded-md mb-6">
            <Author authors={authorsToDisplay} size="small" type="info" />
          </div>
        )}

        {/* Download Button */}
        {href ? (
          <CMSButton
            linkType="link"
            link={{
              type: 'custom',
              url: href,
              newTab: false,
            }}
            label="Download"
            appearance="secondary"
            size="lg"
            className="w-full justify-center mt-auto"
            showIconLeft={true}
            iconLeft="download-2-line"
            showIconRight={false}
          />
        ) : downloadUrl ? (
          <CMSButton
            linkType="link"
            link={{
              type: 'custom',
              url: downloadUrl,
              newTab: true,
            }}
            label="Download"
            appearance="secondary"
            size="lg"
            className="w-full justify-center mt-auto"
            showIconLeft={true}
            iconLeft="download-2-line"
            showIconRight={false}
          />
        ) : (
          <Button variant="secondary" size="lg" className="w-full justify-center mt-auto" disabled>
            <Icon name="download-2-line" className="size-4" />
            <span>Download</span>
          </Button>
        )}
      </div>
    </div>
  )
}
